import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon } from './icons';
import './Sheet.css';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Omit for the completion sheet, which has no header row — dismiss is via
   *  its own primary/secondary buttons, the scrim, or Escape. */
  title?: string;
  children?: React.ReactNode;
  testId?: string;
  /** False for the crawl-chooser welcome sheet, which has no cancel path —
   *  scrim tap, Escape and the close button all become no-ops. */
  dismissible?: boolean;
}

/**
 * The one modal primitive for the app: a bottom sheet on mobile that becomes
 * a centred dialog at desktop widths (CSS only, same markup). Dismisses on
 * close button, scrim tap and Escape; traps focus while open and restores it
 * on close; stays mounted through the exit transition so the animation
 * plays and `waitForSelector(state: 'hidden')` in e2e sees a real state
 * change rather than an instant unmount.
 */
const Sheet: React.FC<SheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  testId,
  dismissible = true,
}) => {
  const [rendered, setRendered] = useState(isOpen);
  const [entered, setEntered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      previouslyFocused.current = document.activeElement as HTMLElement;
      setRendered(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }

    setEntered(false);
    const timeout = setTimeout(() => setRendered(false), 320);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!rendered) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [rendered]);

  useEffect(() => {
    if (entered && panelRef.current) {
      const focusable = panelRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusable ?? panelRef.current).focus();
    }
    if (!rendered && previouslyFocused.current) {
      previouslyFocused.current.focus();
      previouslyFocused.current = null;
    }
  }, [entered, rendered]);

  useEffect(() => {
    if (!rendered) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dismissible) onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [rendered, onClose, dismissible]);

  if (!rendered) return null;

  return (
    <div className="sheet-root" data-testid={testId}>
      <div
        className={`sheet-scrim ${entered ? 'sheet-scrim-on' : ''}`}
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className={`sheet-panel ${entered ? 'sheet-panel-on' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="sheet-grab">
          <span />
        </div>
        {title && (
          <div className="sheet-head">
            <h2>{title}</h2>
            {dismissible && (
              <button className="sheet-close" onClick={onClose} aria-label="Close">
                <CloseIcon />
              </button>
            )}
          </div>
        )}
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
};

export default Sheet;
